import { useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useJobPostingContext } from '@/contexts/job-posting-context';

/**
 * Resolves which openings are already taken by a different product of the same
 * channel and, when every supported opening is taken, produces the user-facing
 * explanation string. Both the channel card (disable + message) and the opening
 * list (per-item disable) need these values.
 */
export function useOpeningsSelectedForAnotherProduct(
  channelKey: string,
  channelSlug: string,
  unsupportedOpenings: string[],
) {
  const intl = useIntl();
  const { hookForm, selectedOpeningIds } = useJobPostingContext();

  const channels = useWatch({ control: hookForm.control, name: 'channels' });

  const openingsSelectedForAnotherProduct = new Set(
    Object.entries(channels ?? {})
      .filter(
        ([key, channel]) => key !== channelKey && channel.slug === channelSlug,
      )
      .flatMap(([, channel]) => Object.keys(channel.openingBudgets)),
  );

  const isAllOpeningsSelectedForAnotherProduct = selectedOpeningIds
    .filter(id => !unsupportedOpenings.includes(id))
    .every(id => openingsSelectedForAnotherProduct.has(id));

  const message = isAllOpeningsSelectedForAnotherProduct
    ? intl.formatMessage({
        defaultMessage:
          'All openings are already selected for another product of the same channel',
        description:
          'Message to show when all openings are already selected for another product of the same channel',
      })
    : null;

  return {
    openingsSelectedForAnotherProduct,
    isAllOpeningsSelectedForAnotherProduct,
    message,
  };
}
